import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import {
  currentVRMAtom,
  languageAtom,
  isOutputTextAtom,
  T2SEngineAtom,
  currentSpeakerAtom,
  voiceSpeedAtom,
  voicePitchAtom,
  voiceIntonationAtom,
  volumeAtom,
  isThinkingAtom,
  isProcessingAtom,
  outputTextAtom,
  isTranscribingAtom,
  isListeningAtom,
  layoutSelectAtom,
  standbyModeAtom,
  isConversationInterruptedAtom,
} from "~/Atom";
import * as icon from "~/Icons";
import { getVRMGlobalName, getVRMImage, getVRMName } from "~/vrm_utils/VRMUtils";
import RubyText from "~/components/utils/RubyText";
import { api } from "~/api";
import MarkdownRenderer from "~/components/utils/MarkdownRenderer";
import { currentLocale, CustomImageType, feedbackModes, talkModes } from "~/Commons";
import {
  customImagesAtom,
  getCurrentCustomerId,
  styleModeAtom,
  feedbackModeAtom,
} from "~/AtomWithDynamoDB";
import { useTranslation } from "react-i18next";
import { emitter } from "~/TTS/voicevox/VoiceSynthesis";
import { PlaySound, playVoice, PlayVoiceParams } from "~/SoundUtils";
import ChatMessageLayout1 from "~/components/layout/ChatMessageLayout1";
import ChatMessageLayout2 from "~/components/layout/ChatMessageLayout2";
import ChatMessageLayout3 from "~/components/layout/ChatMessageLayout3";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import { conversationEmitter } from "~/components/ControlButton";
import { extractDollarKeys, hasDollarKeys, removeDollarKeys } from "~/utils/chatMessageUtils";
import { getCustomImagesByKeysWithSignedUrls } from "~/utils/customImageUtils";

// キャラクターアイコンのサイズ（ピクセル）
const characterIconSize = 40;

// フィードバック状態の定数
const feedbackGood = "good";
const feedbackBad = "bad";
const feedbackNone = "";
const feedbackExpressions = {
  good: "happy",
  bad: "sad",
};

interface ChatMessageProps {
  role: string;
  content: string;
  onImageLoad?: () => void;
  messageId: string;
  contentColor?: string; // RGB色を指定（例: "#dc3545"）
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  onImageLoad,
  messageId,
  contentColor,
  // eslint-disable-next-line complexity
}) => {
  const [currentVRM] = useAtom(currentVRMAtom);

  const [image, setImage] = useState<ImageBitmap | null>(null);
  const [customImagesLoaded, setCustomImagesLoaded] = useState<CustomImageType[]>([]);
  const [customImages] = useAtom(customImagesAtom);
  const [name, setName] = useState<string>("");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [language] = useAtom(languageAtom);
  const [fbSelected, setFbSelected] = useState(feedbackNone);
  const [animationQueue, setAnimationQueue] = useState<string[]>([]);
  // --- タイピングアニメーション用
  const [animatedText, setAnimatedText] = useState<string>("");
  const [, setIsOutputText] = useAtom(isOutputTextAtom);
  const animationTimeout = useRef<NodeJS.Timeout | null>(null);
  const textSpeed = language === "ja-JP" ? 30 : 30; // ms
  const fullTextRef = useRef("");
  // --- TTS用
  const { t } = useTranslation();
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [currentSpeaker] = useAtom(currentSpeakerAtom);
  const [speed] = useAtom(voiceSpeedAtom);
  const [pitch] = useAtom(voicePitchAtom);
  const [intonation] = useAtom(voiceIntonationAtom);
  const [volume] = useAtom(volumeAtom);
  const [, setIsThinking] = useAtom(isThinkingAtom);
  const [, setIsProcessing] = useAtom(isProcessingAtom);
  const [isTranscribing, setIsTranscribing] = useAtom(isTranscribingAtom);
  const [isListening] = useAtom(isListeningAtom);
  const [, setOutputText] = useAtom(outputTextAtom);
  const isFeedbackProcessingRef = useRef(false);
  const [layoutSelect] = useAtom(layoutSelectAtom);
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [, setStandbyMode] = useAtom(standbyModeAtom);
  const [, setIsConversationInterrupted] = useAtom(isConversationInterruptedAtom);
  const [styleMode] = useAtom(styleModeAtom);
  const [feedbackMode] = useAtom(feedbackModeAtom);

  // キャラクター情報を取得・設定する
  useEffect(() => {
    const fetchCharacterName = async () => {
      if (currentVRM !== null) {
        const cachedImage = getVRMImage(currentVRM);
        let cachedName = getVRMName(currentVRM);

        // 言語に応じて名前を取得
        if (language !== currentLocale) {
          cachedName = await getVRMGlobalName(currentVRM);
        }

        if (cachedImage) {
          setImage(cachedImage);
          setName(cachedName);
        }
      }
    };

    fetchCharacterName();
  }, [currentVRM, language]);

  // コンテンツ内のカスタムイメージキーを検出して対応する画像をセットする
  useEffect(() => {
    if (!hasDollarKeys(content)) {
      return;
    }

    const getContentCustomImages = async () => {
      // keyがあれば、対応するカスタム画像のURLを取得
      const keyTexts = extractDollarKeys(content);
      const systemRequirement = process.env.REACT_APP_SYSTEM_REQUIREMENT;
      const images = await getCustomImagesByKeysWithSignedUrls(
        keyTexts,
        customImages,
        systemRequirement
      );
      setCustomImagesLoaded(images);
    };

    getContentCustomImages();
  }, [content, customImages]);

  // name更新時に通知イベントを発火
  useEffect(() => {
    if (name) {
      window.dispatchEvent(
        new CustomEvent("aiConcierge:characterNameUpdated", {
          detail: { name, currentVRM },
        })
      );
    }
  }, [name, currentVRM]);

  // キャラクターアイコンをキャンバスに描画する
  useEffect(() => {
    if (image && canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        canvasRef.current.width = characterIconSize;
        canvasRef.current.height = characterIconSize;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(image, 0, 0, characterIconSize, characterIconSize);
      }
    }
  }, [image, canvasRef.current]);

  // --- タイピングアニメーション制御 ---
  useEffect(() => {
    // if (isImgTag(content)) {
    //   setAnimatedText("");
    //   fullTextRef.current = content;
    //   return;
    // }
    // テキストから $$key$$ を削除
    const removeDollarWrapped = removeDollarKeys(content);
    const newPart = removeDollarWrapped.slice(fullTextRef.current.length);
    if (newPart.length > 0) {
      setAnimationQueue((prev) => [...prev, newPart]);
      fullTextRef.current = removeDollarWrapped;
    }
  }, [content]);

  // imgタグ以外（テキスト）のみタイピング
  useEffect(() => {
    if (messageId === "0" || animationQueue.length === 0) {
      // setIsOutputText(false);
      return;
    }
    let idx = 1;
    let displayTimeout: NodeJS.Timeout;
    setIsOutputText(true);
    const showNext = async () => {
      const nextText = animationQueue[0];
      const preText = animatedText;
      // await setAnimatedText(preText + nextText.slice(0, idx) + "■");
      displayTimeout = setTimeout(async () => {
        idx++;
        await setAnimatedText(preText + nextText.slice(0, idx));

        if (idx <= nextText.length) {
          animationTimeout.current = setTimeout(showNext, textSpeed);
        } else {
          // アニメーション完了
          setIsOutputText(false);
          setAnimationQueue((prev) => prev.slice(1)); // キューを進める
          clearInterval(displayTimeout);
        }
      }, textSpeed);
    };

    showNext();

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(displayTimeout);
      if (animationTimeout.current) {
        clearTimeout(animationTimeout.current);
        animationTimeout.current = null;
      }
    };
  }, [animationQueue]);

  // コンテンツがimg要素かどうかを判定する
  const isImgTag = (text: string): boolean => {
    return /^<img\s+/i.test(text);
  };

  // フィードバックが表示される条件で会話中断を無効にする
  useEffect(() => {
    if (role === "assistant" && messageId !== "" && messageId !== "0") {
      setIsConversationInterrupted(false);
    }
  }, [role, messageId]);

  // 会話中断イベントを監視してタイピングアニメーションを一括表示する
  useEffect(() => {
    /**
     * タイピング中断処理
     * @returns void
     */
    const handleInterruptTyping = () => {
      if (messageId === "0") {
        return;
      }

      // タイムアウトをクリアしてアニメーションを停止
      if (animationTimeout.current) {
        clearTimeout(animationTimeout.current);
        animationTimeout.current = null;
      }

      // 全文を表示してキューをクリア
      setAnimatedText(fullTextRef.current);
      setAnimationQueue([]);
      setIsOutputText(false);
    };

    // ControlButtonからのinterruptTypingイベントをリッスン
    conversationEmitter.on("interruptTyping", handleInterruptTyping);

    return () => {
      conversationEmitter.removeListener("interruptTyping", handleInterruptTyping);
    };
  }, [messageId]);

  /**
   * 星評価フィードバック
   * 選択星を再クリックすると解除、別の星を選ぶと評価値を切り替える
   *
   * @param star 1~5の星評価値
   * @returns void
   */
  const starFB = async (star: number) => {
    // フィードバック処理中なら何もしない
    if (isFeedbackProcessingRef.current) return;
    isFeedbackProcessingRef.current = true;

    try {
      const customerId = await getCurrentCustomerId();
      const starStr = String(star) as "1" | "2" | "3" | "4" | "5";

      // 同じ星を再選択した場合はフィードバックを解除
      if (fbSelected === starStr) {
        const result = await api.dify.feedback.mutate({
          customerId,
          messageId,
          feedback: null,
          content: null,
        });
        if (result) setFbSelected(feedbackNone);
        return;
      }
      // 星1~2: dislike, 星3~5: like
      const rating = star >= 3 ? "like" : "dislike";
      const result = await api.dify.feedback.mutate({
        customerId,
        messageId,
        feedback: rating,
        content: starStr,
      });
      if (result) {
        setFbSelected(starStr);
        emitter.emit(
          "expression",
          rating === "like" ? feedbackExpressions.good : feedbackExpressions.bad
        );
        await playFeedbackVoice(rating === "like" ? feedbackGood : feedbackBad);
      }
    } finally {
      isFeedbackProcessingRef.current = false;
    }
  };

  // 良いフィードバックを送信する
  const goodFB = async () => {
    // フィードバック処理中なら何もしない
    if (isFeedbackProcessingRef.current) return;
    isFeedbackProcessingRef.current = true;

    try {
      // customerId を取得
      const customerId = await getCurrentCustomerId();

      if (fbSelected === feedbackGood) {
        const result = await api.dify.feedback.mutate({
          customerId,
          messageId: messageId,
          feedback: null,
        });
        if (result) setFbSelected(feedbackNone);
        return;
      }
      const result = await api.dify.feedback.mutate({
        customerId,
        messageId: messageId,
        feedback: "like",
      });
      if (result) {
        setFbSelected(feedbackGood);
        emitter.emit("expression", feedbackExpressions.good);
        await playFeedbackVoice(feedbackGood);
      }
    } finally {
      // 処理完了後にフラグをリセット
      isFeedbackProcessingRef.current = false;
    }
  };

  // 悪いフィードバックを送信する
  const badFB = async () => {
    // フィードバック処理中なら何もしない
    if (isFeedbackProcessingRef.current) return;
    isFeedbackProcessingRef.current = true;

    try {
      // customerId を取得
      const customerId = await getCurrentCustomerId();

      if (fbSelected === feedbackBad) {
        const result = await api.dify.feedback.mutate({
          customerId,
          messageId: messageId,
          feedback: null,
        });
        if (result) setFbSelected(feedbackNone);
        return;
      }
      const result = await api.dify.feedback.mutate({
        customerId,
        messageId: messageId,
        feedback: "dislike",
      });
      if (result) {
        setFbSelected(feedbackBad);
        emitter.emit("expression", feedbackExpressions.bad);
        await playFeedbackVoice(feedbackBad);
      }
    } finally {
      // 処理完了後にフラグをリセット
      isFeedbackProcessingRef.current = false;
    }
  };

  // imgタグからsrc属性を抽出する
  const extractImgProps = (text: string): string => {
    const decodedText = text.replace(/\\"/g, '"');
    const srcMatch = decodedText.match(/<img\s+.*?src=(?:"|')([^"']+)(?:"|')/i);
    return srcMatch ? srcMatch[1] : "";
  };

  // フィードバック用の音声再生
  const playFeedbackVoice = async (feedbackType: string) => {
    // 文字起こしを無効に設定
    setIsTranscribing(false);

    const feedbackMessages = {
      good: t(`feedbackVoice.${styleMode}.good`),
      bad: t(`feedbackVoice.${styleMode}.bad`),
    };

    const message = feedbackMessages[feedbackType as keyof typeof feedbackMessages];

    if (message) {
      const params: PlayVoiceParams = {
        inputText: message,
        T2SEngine,
        currentSpeaker,
        speed,
        pitch,
        intonation,
        volume,
        talkingMode: 9, // その他として扱う
        setIsThinking,
        setIsProcessing,
        setIsTranscribing,
        setOutputText,
      };

      await playVoice(params);
    }
  };

  const onClickClearSession = () => {
    PlaySound("push", volume);
    setIsSessionModalOpen(true);
  };

  // セッションクリアモーダルを閉じる
  const closeSessionModal = () => {
    PlaySound("push", volume);
    setIsSessionModalOpen(false);
    setStandbyMode(talkModes.active);
  };

  // セッションをクリア
  const clearSession = () => {
    PlaySound("push", volume);
    setIsSessionModalOpen(false);
    setStandbyMode(talkModes.standby);
  };

  switch (layoutSelect) {
    case 1:
      return (
        <ChatMessageLayout1
          role={role}
          content={content}
          onImageLoad={onImageLoad}
          messageId={messageId}
          image={image}
          canvasRef={canvasRef}
          name={name}
          isImgTag={isImgTag}
          extractImgProps={extractImgProps}
          animatedText={animatedText}
          t={t}
          isTranscribing={isTranscribing}
          isListening={isListening}
          isFeedbackProcessingRef={isFeedbackProcessingRef}
          goodFB={goodFB}
          badFB={badFB}
          starFB={starFB}
          feedbackMode={feedbackMode}
          fbSelected={fbSelected}
          feedbackGood={feedbackGood}
          feedbackBad={feedbackBad}
          onClickClearSession={onClickClearSession}
          isSessionModalOpen={isSessionModalOpen}
          closeSessionModal={closeSessionModal}
          clearSession={clearSession}
          contentColor={contentColor}
          customImagesLoaded={customImagesLoaded}
        />
      );
    case 2:
      return (
        <ChatMessageLayout2
          role={role}
          content={content}
          onImageLoad={onImageLoad}
          messageId={messageId}
          image={image}
          canvasRef={canvasRef}
          name={name}
          isImgTag={isImgTag}
          extractImgProps={extractImgProps}
          animatedText={animatedText}
          t={t}
          isTranscribing={isTranscribing}
          isListening={isListening}
          isFeedbackProcessingRef={isFeedbackProcessingRef}
          goodFB={goodFB}
          badFB={badFB}
          starFB={starFB}
          feedbackMode={feedbackMode}
          fbSelected={fbSelected}
          feedbackGood={feedbackGood}
          feedbackBad={feedbackBad}
          characterName={name}
          onClickClearSession={onClickClearSession}
          isSessionModalOpen={isSessionModalOpen}
          closeSessionModal={closeSessionModal}
          clearSession={clearSession}
          contentColor={contentColor}
          customImagesLoaded={customImagesLoaded}
        />
      );
    case 3:
      return (
        <ChatMessageLayout3
          role={role}
          content={content}
          onImageLoad={onImageLoad}
          messageId={messageId}
          image={image}
          canvasRef={canvasRef}
          isImgTag={isImgTag}
          extractImgProps={extractImgProps}
          animatedText={animatedText}
          t={t}
          isTranscribing={isTranscribing}
          isListening={isListening}
          isFeedbackProcessingRef={isFeedbackProcessingRef}
          goodFB={goodFB}
          badFB={badFB}
          starFB={starFB}
          feedbackMode={feedbackMode}
          fbSelected={fbSelected}
          feedbackGood={feedbackGood}
          feedbackBad={feedbackBad}
          onClickClearSession={onClickClearSession}
          isSessionModalOpen={isSessionModalOpen}
          closeSessionModal={closeSessionModal}
          clearSession={clearSession}
          contentColor={contentColor}
          customImagesLoaded={customImagesLoaded}
        />
      );
    default:
      return (
        <div className={`message ${role === "user" ? "user" : "assistant"}`}>
          <div className="message-content">
            {role === "assistant" && image && (
              <div className="character-info">
                <div className="message-image">
                  <canvas ref={canvasRef}></canvas>
                </div>
                <RubyText text={name} />
              </div>
            )}

            {isImgTag(content) ? (
              <div className="qr-code">
                <img src={extractImgProps(content)} alt="二次元コード" onLoad={onImageLoad} />
              </div>
            ) : (
              <>
                {/* テキスト部分をアニメーション表示 */}
                <span
                  className="content"
                  style={contentColor ? { color: contentColor } : undefined}
                >
                  {messageId === "0" ? (
                    <MarkdownRenderer content={content} />
                  ) : (
                    <MarkdownRenderer content={animatedText} />
                  )}

                  {/* タイピングアニメーションのテキスト */}
                </span>
              </>
            )}

            {/* カスタムイメージがあれば表示 */}
            {role === "assistant" &&
              customImagesLoaded &&
              customImagesLoaded.map((customImage, index) => (
                <div key={`${customImage.key}-${index}`} className="custom-image">
                  <img src={customImage.src} alt={customImage.key} />
                </div>
              ))}

            {role === "assistant" && messageId !== "" && messageId !== "0" && (
              <div className="feedback-container">
                <div className="message-review">
                  <span className="feedback-question">{t("usabilityReview")}</span>
                  {feedbackMode === feedbackModes.detail ? (
                    /* 5段階モード: 星評価 */
                    <div className="feedback-stars" data-testid="feedback-stars">
                      {[1, 2, 3, 4, 5].map((star) => {
                        const isActive =
                          isTranscribing && isListening && !isFeedbackProcessingRef.current;
                        return (
                          <span
                            key={star}
                            onClick={isActive ? () => starFB(star) : undefined}
                            data-testid={`fb-star-${star}`}
                            className={`material-symbols-rounded feedback-star ${fbSelected === String(star) ? "selected" : ""} ${Number(fbSelected) > 0 && star <= Number(fbSelected) ? "feedback-star--filled" : "feedback-star--empty"}`}
                            style={{
                              opacity: isActive ? 1 : 0.5,
                              cursor: isActive ? "pointer" : "initial",
                            }}
                          >
                            star
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    /* シンプルモード: GOOD/BAD */
                    <div className="feedback-icons">
                      <span
                        onClick={
                          isTranscribing && isListening && !isFeedbackProcessingRef.current
                            ? goodFB
                            : undefined
                        }
                        className={`material-symbols-rounded feedback-thumbs ${fbSelected === feedbackGood ? "selected" : ""}`}
                        style={{
                          opacity:
                            isTranscribing && isListening && !isFeedbackProcessingRef.current
                              ? 1
                              : 0.5,
                          cursor:
                            isTranscribing && isListening && !isFeedbackProcessingRef.current
                              ? "pointer"
                              : "initial",
                        }}
                      >
                        {fbSelected === feedbackGood ? <icon.icons_fb_good /> : "thumb_up"}
                      </span>
                      <span
                        onClick={
                          isTranscribing && isListening && !isFeedbackProcessingRef.current
                            ? badFB
                            : undefined
                        }
                        className={`material-symbols-rounded feedback-thumbs ${fbSelected === feedbackBad ? "selected" : ""}`}
                        style={{
                          opacity:
                            isTranscribing && isListening && !isFeedbackProcessingRef.current
                              ? 1
                              : 0.5,
                          cursor:
                            isTranscribing && isListening && !isFeedbackProcessingRef.current
                              ? "pointer"
                              : "initial",
                        }}
                      >
                        {fbSelected === feedbackBad ? <icon.icons_fb_bad /> : "thumb_down"}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <button className="button-1" onClick={() => onClickClearSession()}>
                    {t("clearSession")}
                  </button>
                </div>
              </div>
            )}
          </div>
          <ConfirmationModal
            show={isSessionModalOpen}
            handleClose={() => closeSessionModal()}
            handleConfirm={() => clearSession()}
            title={t("clearSession")}
            body={t("clearSessionDescription")}
            cancelText={t("cancel")}
            confirmText={t("reset")}
          />
        </div>
      );
  }
};

export default ChatMessage;
